import * as THREE from 'three';
import { CelestialConfig, CelestialType, PhysicsBody, RingConfig, AtmosphereConfig } from '../types';

/**
 * Base class for all celestial bodies (stars, planets, moons, asteroids, ships).
 * Manages 3D mesh, physics body, and visual effects.
 */
export class CelestialBody {
  public readonly id: string;
  public readonly type: CelestialType;
  public readonly name: string;
  public readonly config: CelestialConfig;

  // Three.js objects
  public readonly mesh: THREE.Group;
  private mainMesh: THREE.Mesh;
  private ringMesh?: THREE.Mesh;
  private atmosphereMesh?: THREE.Mesh;
  private glowMesh?: THREE.Mesh;
  private cloudMesh?: THREE.Mesh;
  private coronaMesh?: THREE.Mesh;

  // Physics
  public physicsBody: PhysicsBody;

  // Visual state
  private selected: boolean = false;
  private selectionRing?: THREE.Line;

  // Orbit trail
  private orbitTrail: THREE.Vector3[] = [];
  private orbitLine?: THREE.Line;
  private readonly maxTrailPoints: number = 500;

  // Animation
  private animTime: number = 0;

  constructor(config: CelestialConfig) {
    this.id = config.id;
    this.type = config.type;
    this.name = config.name;
    this.config = config;

    // Create container group
    this.mesh = new THREE.Group();
    this.mesh.name = config.name;
    this.mesh.userData = { celestialBody: this };

    // Create main mesh based on type
    this.mainMesh = this.createMainMesh();
    this.mesh.add(this.mainMesh);

    // Add optional components
    if (config.rings) {
      this.ringMesh = this.createRings(config.rings);
      this.mesh.add(this.ringMesh);
    }

    if (config.atmosphere) {
      this.atmosphereMesh = this.createAtmosphere(config.atmosphere);
      this.mesh.add(this.atmosphereMesh);

      // Add cloud layer for planets with atmosphere
      if (config.type === 'planet') {
        this.cloudMesh = this.createCloudLayer(config.atmosphere);
        this.mesh.add(this.cloudMesh);
      }
    }

    if (config.type === 'star') {
      this.glowMesh = this.createGlow();
      this.mesh.add(this.glowMesh);

      // Add corona for stars
      this.coronaMesh = this.createCorona();
      this.mesh.add(this.coronaMesh);
    }

    // Create physics body
    this.physicsBody = this.createPhysicsBody();

    // Set initial position
    this.mesh.position.set(config.position.x, config.position.y, config.position.z);
  }

  private createMainMesh(): THREE.Mesh {
    let geometry: THREE.BufferGeometry;
    let material: THREE.Material;

    switch (this.type) {
      case 'star':
        geometry = new THREE.SphereGeometry(this.config.radius, 64, 64);
        material = this.createStarMaterial();
        break;

      case 'asteroid':
      case 'comet':
        geometry = this.createAsteroidGeometry();
        material = this.createAsteroidMaterial();
        break;

      case 'ship':
        geometry = this.createShipGeometry();
        material = new THREE.MeshStandardMaterial({
          color: this.config.color,
          roughness: 0.3,
          metalness: 0.8,
        });
        break;

      default: // planet, moon
        geometry = new THREE.SphereGeometry(this.config.radius, 128, 128);
        material = this.createPlanetMaterial();
    }

    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;

    return mesh;
  }

  private createStarMaterial(): THREE.ShaderMaterial {
    const color = new THREE.Color(this.config.emissive ?? this.config.color);

    return new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 },
        baseColor: { value: color },
      },
      vertexShader: `
        varying vec2 vUv;
        varying vec3 vNormal;
        varying vec3 vPosition;

        void main() {
          vUv = uv;
          vNormal = normalize(normalMatrix * normal);
          vPosition = position;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float time;
        uniform vec3 baseColor;
        varying vec2 vUv;
        varying vec3 vNormal;
        varying vec3 vPosition;

        // Simplex noise function
        vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
        vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
        vec4 permute(vec4 x) { return mod289(((x*34.0)+1.0)*x); }
        vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

        float snoise(vec3 v) {
          const vec2 C = vec2(1.0/6.0, 1.0/3.0);
          const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
          vec3 i  = floor(v + dot(v, C.yyy));
          vec3 x0 = v - i + dot(i, C.xxx);
          vec3 g = step(x0.yzx, x0.xyz);
          vec3 l = 1.0 - g;
          vec3 i1 = min(g.xyz, l.zxy);
          vec3 i2 = max(g.xyz, l.zxy);
          vec3 x1 = x0 - i1 + C.xxx;
          vec3 x2 = x0 - i2 + C.yyy;
          vec3 x3 = x0 - D.yyy;
          i = mod289(i);
          vec4 p = permute(permute(permute(
            i.z + vec4(0.0, i1.z, i2.z, 1.0))
            + i.y + vec4(0.0, i1.y, i2.y, 1.0))
            + i.x + vec4(0.0, i1.x, i2.x, 1.0));
          float n_ = 0.142857142857;
          vec3 ns = n_ * D.wyz - D.xzx;
          vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
          vec4 x_ = floor(j * ns.z);
          vec4 y_ = floor(j - 7.0 * x_);
          vec4 x = x_ *ns.x + ns.yyyy;
          vec4 y = y_ *ns.x + ns.yyyy;
          vec4 h = 1.0 - abs(x) - abs(y);
          vec4 b0 = vec4(x.xy, y.xy);
          vec4 b1 = vec4(x.zw, y.zw);
          vec4 s0 = floor(b0)*2.0 + 1.0;
          vec4 s1 = floor(b1)*2.0 + 1.0;
          vec4 sh = -step(h, vec4(0.0));
          vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
          vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;
          vec3 p0 = vec3(a0.xy, h.x);
          vec3 p1 = vec3(a0.zw, h.y);
          vec3 p2 = vec3(a1.xy, h.z);
          vec3 p3 = vec3(a1.zw, h.w);
          vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
          p0 *= norm.x;
          p1 *= norm.y;
          p2 *= norm.z;
          p3 *= norm.w;
          vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
          m = m * m;
          return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
        }

        void main() {
          // Animated turbulence for solar surface
          float noise1 = snoise(vPosition * 0.5 + time * 0.1);
          float noise2 = snoise(vPosition * 1.0 + time * 0.15) * 0.5;
          float noise3 = snoise(vPosition * 2.0 + time * 0.2) * 0.25;
          float turbulence = noise1 + noise2 + noise3;

          // Create sunspot-like patterns
          float spots = smoothstep(0.3, 0.5, snoise(vPosition * 0.3 + time * 0.02));

          // Color variation
          vec3 hotColor = vec3(1.0, 0.95, 0.8);
          vec3 coolColor = baseColor * 0.8;
          vec3 color = mix(coolColor, hotColor, 0.5 + turbulence * 0.3);
          color = mix(color, baseColor * 0.5, spots * 0.3);

          // Limb darkening
          float limb = 1.0 - pow(1.0 - dot(vNormal, vec3(0.0, 0.0, 1.0)), 0.5);
          color *= 0.7 + limb * 0.3;

          gl_FragColor = vec4(color, 1.0);
        }
      `,
    });
  }

  private createPlanetMaterial(): THREE.ShaderMaterial {
    const color = new THREE.Color(this.config.color);
    const isGasGiant = this.config.radius > 15;
    const hasWater = color.b > color.r && color.b > color.g;

    return new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 },
        baseColor: { value: color },
        isGasGiant: { value: isGasGiant ? 1.0 : 0.0 },
        hasWater: { value: hasWater ? 1.0 : 0.0 },
        lightDir: { value: new THREE.Vector3(1, 0.5, 1).normalize() },
      },
      vertexShader: `
        varying vec2 vUv;
        varying vec3 vNormal;
        varying vec3 vPosition;
        varying vec3 vWorldNormal;

        void main() {
          vUv = uv;
          vNormal = normalize(normalMatrix * normal);
          vWorldNormal = normalize((modelMatrix * vec4(normal, 0.0)).xyz);
          vPosition = position;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float time;
        uniform vec3 baseColor;
        uniform float isGasGiant;
        uniform float hasWater;
        uniform vec3 lightDir;
        varying vec2 vUv;
        varying vec3 vNormal;
        varying vec3 vPosition;
        varying vec3 vWorldNormal;

        // Noise functions
        vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
        vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
        vec4 permute(vec4 x) { return mod289(((x*34.0)+1.0)*x); }
        vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

        float snoise(vec3 v) {
          const vec2 C = vec2(1.0/6.0, 1.0/3.0);
          const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
          vec3 i  = floor(v + dot(v, C.yyy));
          vec3 x0 = v - i + dot(i, C.xxx);
          vec3 g = step(x0.yzx, x0.xyz);
          vec3 l = 1.0 - g;
          vec3 i1 = min(g.xyz, l.zxy);
          vec3 i2 = max(g.xyz, l.zxy);
          vec3 x1 = x0 - i1 + C.xxx;
          vec3 x2 = x0 - i2 + C.yyy;
          vec3 x3 = x0 - D.yyy;
          i = mod289(i);
          vec4 p = permute(permute(permute(
            i.z + vec4(0.0, i1.z, i2.z, 1.0))
            + i.y + vec4(0.0, i1.y, i2.y, 1.0))
            + i.x + vec4(0.0, i1.x, i2.x, 1.0));
          float n_ = 0.142857142857;
          vec3 ns = n_ * D.wyz - D.xzx;
          vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
          vec4 x_ = floor(j * ns.z);
          vec4 y_ = floor(j - 7.0 * x_);
          vec4 x = x_ *ns.x + ns.yyyy;
          vec4 y = y_ *ns.x + ns.yyyy;
          vec4 h = 1.0 - abs(x) - abs(y);
          vec4 b0 = vec4(x.xy, y.xy);
          vec4 b1 = vec4(x.zw, y.zw);
          vec4 s0 = floor(b0)*2.0 + 1.0;
          vec4 s1 = floor(b1)*2.0 + 1.0;
          vec4 sh = -step(h, vec4(0.0));
          vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
          vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;
          vec3 p0 = vec3(a0.xy, h.x);
          vec3 p1 = vec3(a0.zw, h.y);
          vec3 p2 = vec3(a1.xy, h.z);
          vec3 p3 = vec3(a1.zw, h.w);
          vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
          p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;
          vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
          m = m * m;
          return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
        }

        void main() {
          vec3 color = baseColor;

          if (isGasGiant > 0.5) {
            // Gas giant with bands
            float bands = sin(vPosition.y * 8.0 + snoise(vPosition * 2.0) * 2.0) * 0.5 + 0.5;
            float storm = smoothstep(0.6, 0.8, snoise(vPosition * 0.5 + time * 0.01));

            vec3 bandColor1 = baseColor * 1.2;
            vec3 bandColor2 = baseColor * 0.7;
            vec3 stormColor = vec3(0.9, 0.85, 0.8);

            color = mix(bandColor1, bandColor2, bands);
            color = mix(color, stormColor, storm * 0.5);

            // Add turbulence
            float turb = snoise(vPosition * 4.0 + time * 0.05) * 0.1;
            color += turb;
          } else if (hasWater > 0.5) {
            // Earth-like planet with continents
            float continent = smoothstep(0.0, 0.3, snoise(vPosition * 1.5));
            float mountains = smoothstep(0.5, 0.7, snoise(vPosition * 4.0)) * continent;
            float ice = smoothstep(0.7, 0.9, abs(vPosition.y / length(vPosition)));

            vec3 oceanColor = baseColor;
            vec3 landColor = vec3(0.3, 0.5, 0.2);
            vec3 mountainColor = vec3(0.5, 0.45, 0.4);
            vec3 iceColor = vec3(0.95, 0.98, 1.0);

            color = mix(oceanColor, landColor, continent);
            color = mix(color, mountainColor, mountains);
            color = mix(color, iceColor, ice);
          } else {
            // Rocky/barren planet
            float craters = smoothstep(0.4, 0.5, snoise(vPosition * 3.0));
            float terrain = snoise(vPosition * 2.0) * 0.2;

            color = baseColor * (0.8 + terrain);
            color = mix(color, baseColor * 0.6, craters * 0.4);
          }

          // Lighting
          float diffuse = max(dot(vWorldNormal, lightDir), 0.0);
          float ambient = 0.15;
          color *= ambient + diffuse * 0.85;

          // Fresnel rim
          float fresnel = pow(1.0 - max(dot(vNormal, vec3(0.0, 0.0, 1.0)), 0.0), 3.0);
          color += fresnel * 0.15;

          gl_FragColor = vec4(color, 1.0);
        }
      `,
    });
  }

  private createAsteroidMaterial(): THREE.ShaderMaterial {
    const color = new THREE.Color(this.config.color);

    return new THREE.ShaderMaterial({
      uniforms: {
        baseColor: { value: color },
        lightDir: { value: new THREE.Vector3(1, 0.5, 1).normalize() },
      },
      vertexShader: `
        varying vec3 vNormal;
        varying vec3 vPosition;

        void main() {
          vNormal = normalize(normalMatrix * normal);
          vPosition = position;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 baseColor;
        uniform vec3 lightDir;
        varying vec3 vNormal;
        varying vec3 vPosition;

        float random(vec3 p) {
          return fract(sin(dot(p, vec3(12.9898, 78.233, 45.164))) * 43758.5453);
        }

        void main() {
          // Cratered surface
          float crater = random(floor(vPosition * 5.0)) * 0.3;
          float detail = random(floor(vPosition * 20.0)) * 0.1;

          vec3 color = baseColor * (0.7 + crater + detail);

          // Lighting
          float diffuse = max(dot(vNormal, lightDir), 0.0);
          color *= 0.2 + diffuse * 0.8;

          gl_FragColor = vec4(color, 1.0);
        }
      `,
    });
  }

  private createAsteroidGeometry(): THREE.BufferGeometry {
    // Create irregular asteroid shape by displacing icosahedron vertices
    const geometry = new THREE.IcosahedronGeometry(this.config.radius, 2);
    const positions = geometry.attributes.position;

    for (let i = 0; i < positions.count; i++) {
      const x = positions.getX(i);
      const y = positions.getY(i);
      const z = positions.getZ(i);

      // Random displacement for irregular shape
      const noise = 0.7 + Math.random() * 0.6;
      positions.setXYZ(i, x * noise, y * noise, z * noise);
    }

    geometry.computeVertexNormals();
    return geometry;
  }

  private createShipGeometry(): THREE.BufferGeometry {
    // Simple wedge shape for ships
    const shape = new THREE.Shape();
    shape.moveTo(0, 2);
    shape.lineTo(1, -1);
    shape.lineTo(0, -0.5);
    shape.lineTo(-1, -1);
    shape.closePath();

    const extrudeSettings = {
      depth: 0.5,
      bevelEnabled: true,
      bevelThickness: 0.1,
      bevelSize: 0.1,
      bevelSegments: 2,
    };

    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    geometry.scale(this.config.radius, this.config.radius, this.config.radius);
    geometry.rotateX(Math.PI / 2);

    return geometry;
  }

  private createRings(ringConfig: RingConfig): THREE.Mesh {
    const geometry = new THREE.RingGeometry(
      ringConfig.innerRadius,
      ringConfig.outerRadius,
      128,
      8
    );

    // Rotate ring to be horizontal
    geometry.rotateX(-Math.PI / 2);

    // Create procedural ring texture with gaps and bands
    const color = new THREE.Color(ringConfig.color);

    const material = new THREE.ShaderMaterial({
      uniforms: {
        baseColor: { value: color },
        innerRadius: { value: ringConfig.innerRadius },
        outerRadius: { value: ringConfig.outerRadius },
        time: { value: 0 },
      },
      vertexShader: `
        varying vec2 vUv;
        varying vec3 vPosition;

        void main() {
          vUv = uv;
          vPosition = position;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 baseColor;
        uniform float innerRadius;
        uniform float outerRadius;
        uniform float time;
        varying vec2 vUv;
        varying vec3 vPosition;

        float random(float x) {
          return fract(sin(x * 12.9898) * 43758.5453);
        }

        void main() {
          // Calculate distance from center for ring bands
          float dist = length(vPosition.xz);
          float normalizedDist = (dist - innerRadius) / (outerRadius - innerRadius);

          // Create multiple ring bands with gaps
          float bands = 0.0;
          bands += smoothstep(0.0, 0.05, normalizedDist) * smoothstep(0.15, 0.1, normalizedDist);
          bands += smoothstep(0.18, 0.22, normalizedDist) * smoothstep(0.45, 0.4, normalizedDist);
          bands += smoothstep(0.5, 0.55, normalizedDist) * smoothstep(0.7, 0.65, normalizedDist);
          bands += smoothstep(0.75, 0.8, normalizedDist) * smoothstep(1.0, 0.95, normalizedDist);

          // Add fine detail/dust
          float detail = random(floor(dist * 50.0)) * 0.3 + 0.7;

          // Color variation across rings
          vec3 color = baseColor;
          color = mix(color * 0.8, color * 1.2, normalizedDist);
          color *= detail;

          // Transparency with bands
          float alpha = bands * 0.7;

          // Fade at edges
          alpha *= smoothstep(0.0, 0.1, normalizedDist);
          alpha *= smoothstep(1.0, 0.9, normalizedDist);

          gl_FragColor = vec4(color, alpha);
        }
      `,
      transparent: true,
      side: THREE.DoubleSide,
      depthWrite: false,
    });

    return new THREE.Mesh(geometry, material);
  }

  private createAtmosphere(atmosphereConfig: AtmosphereConfig): THREE.Mesh {
    const geometry = new THREE.SphereGeometry(
      this.config.radius + atmosphereConfig.height,
      64,
      64
    );

    const color = new THREE.Color(atmosphereConfig.color);

    const material = new THREE.ShaderMaterial({
      uniforms: {
        atmosphereColor: { value: color },
        density: { value: atmosphereConfig.density },
      },
      vertexShader: `
        varying vec3 vNormal;
        varying vec3 vPosition;

        void main() {
          vNormal = normalize(normalMatrix * normal);
          vPosition = (modelViewMatrix * vec4(position, 1.0)).xyz;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 atmosphereColor;
        uniform float density;
        varying vec3 vNormal;
        varying vec3 vPosition;

        void main() {
          // Fresnel effect for atmospheric rim
          vec3 viewDir = normalize(-vPosition);
          float fresnel = pow(1.0 - abs(dot(vNormal, viewDir)), 3.0);

          // Scattering effect
          vec3 color = atmosphereColor;
          float alpha = fresnel * density * 0.8;

          // Add subtle color variation
          color = mix(color, vec3(1.0), fresnel * 0.3);

          gl_FragColor = vec4(color, alpha);
        }
      `,
      transparent: true,
      side: THREE.BackSide,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });

    return new THREE.Mesh(geometry, material);
  }

  private createCloudLayer(atmosphereConfig: AtmosphereConfig): THREE.Mesh {
    const geometry = new THREE.SphereGeometry(
      this.config.radius * 1.02,
      64,
      64
    );

    const material = new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 },
        cloudColor: { value: new THREE.Color(0xffffff) },
      },
      vertexShader: `
        varying vec2 vUv;
        varying vec3 vNormal;
        varying vec3 vPosition;

        void main() {
          vUv = uv;
          vNormal = normalize(normalMatrix * normal);
          vPosition = position;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float time;
        uniform vec3 cloudColor;
        varying vec2 vUv;
        varying vec3 vNormal;
        varying vec3 vPosition;

        // Noise functions
        vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
        vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
        vec4 permute(vec4 x) { return mod289(((x*34.0)+1.0)*x); }
        vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

        float snoise(vec3 v) {
          const vec2 C = vec2(1.0/6.0, 1.0/3.0);
          const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
          vec3 i  = floor(v + dot(v, C.yyy));
          vec3 x0 = v - i + dot(i, C.xxx);
          vec3 g = step(x0.yzx, x0.xyz);
          vec3 l = 1.0 - g;
          vec3 i1 = min(g.xyz, l.zxy);
          vec3 i2 = max(g.xyz, l.zxy);
          vec3 x1 = x0 - i1 + C.xxx;
          vec3 x2 = x0 - i2 + C.yyy;
          vec3 x3 = x0 - D.yyy;
          i = mod289(i);
          vec4 p = permute(permute(permute(
            i.z + vec4(0.0, i1.z, i2.z, 1.0))
            + i.y + vec4(0.0, i1.y, i2.y, 1.0))
            + i.x + vec4(0.0, i1.x, i2.x, 1.0));
          float n_ = 0.142857142857;
          vec3 ns = n_ * D.wyz - D.xzx;
          vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
          vec4 x_ = floor(j * ns.z);
          vec4 y_ = floor(j - 7.0 * x_);
          vec4 x = x_ *ns.x + ns.yyyy;
          vec4 y = y_ *ns.x + ns.yyyy;
          vec4 h = 1.0 - abs(x) - abs(y);
          vec4 b0 = vec4(x.xy, y.xy);
          vec4 b1 = vec4(x.zw, y.zw);
          vec4 s0 = floor(b0)*2.0 + 1.0;
          vec4 s1 = floor(b1)*2.0 + 1.0;
          vec4 sh = -step(h, vec4(0.0));
          vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
          vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;
          vec3 p0 = vec3(a0.xy, h.x);
          vec3 p1 = vec3(a0.zw, h.y);
          vec3 p2 = vec3(a1.xy, h.z);
          vec3 p3 = vec3(a1.zw, h.w);
          vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
          p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;
          vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
          m = m * m;
          return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
        }

        void main() {
          // Animated cloud patterns
          vec3 animPos = vPosition + vec3(time * 0.02, 0.0, time * 0.01);
          float cloud1 = snoise(animPos * 2.0) * 0.5 + 0.5;
          float cloud2 = snoise(animPos * 4.0 + 10.0) * 0.5 + 0.5;
          float clouds = smoothstep(0.4, 0.6, cloud1 * cloud2);

          // Less clouds near poles
          float latitude = abs(vPosition.y / length(vPosition));
          clouds *= 1.0 - smoothstep(0.6, 0.9, latitude) * 0.5;

          vec3 color = cloudColor;
          float alpha = clouds * 0.6;

          gl_FragColor = vec4(color, alpha);
        }
      `,
      transparent: true,
      side: THREE.FrontSide,
      depthWrite: false,
    });

    return new THREE.Mesh(geometry, material);
  }

  private createCorona(): THREE.Mesh {
    const geometry = new THREE.SphereGeometry(this.config.radius * 4, 32, 32);
    const color = new THREE.Color(this.config.emissive ?? this.config.color);

    const material = new THREE.ShaderMaterial({
      uniforms: {
        coronaColor: { value: color },
        time: { value: 0 },
      },
      vertexShader: `
        varying vec3 vNormal;
        varying vec3 vPosition;

        void main() {
          vNormal = normalize(normalMatrix * normal);
          vPosition = (modelViewMatrix * vec4(position, 1.0)).xyz;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 coronaColor;
        uniform float time;
        varying vec3 vNormal;
        varying vec3 vPosition;

        void main() {
          vec3 viewDir = normalize(-vPosition);
          float fresnel = pow(1.0 - abs(dot(vNormal, viewDir)), 4.0);

          // Animated solar flares
          float angle = atan(vPosition.y, vPosition.x);
          float flare1 = pow(max(sin(angle * 3.0 + time * 0.5), 0.0), 8.0);
          float flare2 = pow(max(sin(angle * 5.0 - time * 0.3), 0.0), 6.0);
          float flares = (flare1 + flare2 * 0.5) * 0.3;

          vec3 color = coronaColor * fresnel;
          color += coronaColor * flares * fresnel;

          float alpha = fresnel * 0.15 + flares * 0.1;

          gl_FragColor = vec4(color, alpha);
        }
      `,
      side: THREE.BackSide,
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthWrite: false,
    });

    return new THREE.Mesh(geometry, material);
  }

  private createGlow(): THREE.Mesh {
    const geometry = new THREE.SphereGeometry(this.config.radius * 2.5, 64, 64);
    const color = new THREE.Color(this.config.emissive ?? this.config.color);

    const material = new THREE.ShaderMaterial({
      uniforms: {
        glowColor: { value: color },
        time: { value: 0 },
      },
      vertexShader: `
        varying vec3 vNormal;
        varying vec3 vPosition;

        void main() {
          vNormal = normalize(normalMatrix * normal);
          vPosition = (modelViewMatrix * vec4(position, 1.0)).xyz;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 glowColor;
        uniform float time;
        varying vec3 vNormal;
        varying vec3 vPosition;

        void main() {
          vec3 viewDir = normalize(-vPosition);
          float fresnel = pow(1.0 - abs(dot(vNormal, viewDir)), 2.5);

          // Corona rays effect
          float angle = atan(vPosition.y, vPosition.x);
          float rays = sin(angle * 12.0 + time) * 0.1 + 0.9;

          vec3 color = glowColor * fresnel * rays;

          // Hotter color at center
          vec3 hotColor = vec3(1.0, 0.95, 0.8);
          color = mix(color, hotColor * fresnel, fresnel * 0.5);

          float alpha = fresnel * 0.7;

          gl_FragColor = vec4(color, alpha);
        }
      `,
      side: THREE.BackSide,
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthWrite: false,
    });

    return new THREE.Mesh(geometry, material);
  }

  private createPhysicsBody(): PhysicsBody {
    return {
      id: this.id,
      position: new THREE.Vector3(
        this.config.position.x,
        this.config.position.y,
        this.config.position.z
      ),
      velocity: new THREE.Vector3(
        this.config.velocity.x,
        this.config.velocity.y,
        this.config.velocity.z
      ),
      acceleration: new THREE.Vector3(),
      mass: this.config.mass,
      radius: this.config.radius,
      isStatic: this.config.isStatic ?? false,
      object3D: this.mesh,
    };
  }

  // Public API

  public update(deltaTime: number): void {
    this.animTime += deltaTime;

    // Rotate the body
    if (this.type === 'planet' || this.type === 'moon') {
      this.mainMesh.rotation.y += deltaTime * 0.1;
    } else if (this.type === 'asteroid') {
      this.mainMesh.rotation.x += deltaTime * 0.2;
      this.mainMesh.rotation.y += deltaTime * 0.15;
    }

    // Update shader time uniforms
    if (this.mainMesh.material instanceof THREE.ShaderMaterial) {
      if (this.mainMesh.material.uniforms.time) {
        this.mainMesh.material.uniforms.time.value = this.animTime;
      }
    }

    // Update glow shader
    if (this.glowMesh) {
      const material = this.glowMesh.material as THREE.ShaderMaterial;
      if (material.uniforms.time) {
        material.uniforms.time.value = this.animTime;
      }
    }

    // Update ring shader
    if (this.ringMesh) {
      const material = this.ringMesh.material as THREE.ShaderMaterial;
      if (material.uniforms && material.uniforms.time) {
        material.uniforms.time.value = this.animTime;
      }
    }

    // Update cloud layer
    if (this.cloudMesh) {
      const material = this.cloudMesh.material as THREE.ShaderMaterial;
      if (material.uniforms && material.uniforms.time) {
        material.uniforms.time.value = this.animTime;
      }
      // Rotate clouds slightly faster than planet
      this.cloudMesh.rotation.y += deltaTime * 0.03;
    }

    // Update corona
    if (this.coronaMesh) {
      const material = this.coronaMesh.material as THREE.ShaderMaterial;
      if (material.uniforms && material.uniforms.time) {
        material.uniforms.time.value = this.animTime;
      }
    }

    // Update orbit trail
    this.updateOrbitTrail();
  }

  private updateOrbitTrail(): void {
    if (this.physicsBody.isStatic) return;

    // Add current position to trail
    this.orbitTrail.push(this.mesh.position.clone());

    // Limit trail length
    if (this.orbitTrail.length > this.maxTrailPoints) {
      this.orbitTrail.shift();
    }

    // Update or create trail line
    if (this.orbitTrail.length > 1) {
      if (this.orbitLine) {
        this.mesh.parent?.remove(this.orbitLine);
        this.orbitLine.geometry.dispose();
        (this.orbitLine.material as THREE.Material).dispose();
      }

      const geometry = new THREE.BufferGeometry().setFromPoints(this.orbitTrail);
      const material = new THREE.LineBasicMaterial({
        color: this.config.color,
        transparent: true,
        opacity: 0.7, // Much brighter
        linewidth: 2, // Note: linewidth only works in some renderers
      });

      this.orbitLine = new THREE.Line(geometry, material);
      this.mesh.parent?.add(this.orbitLine);
    }
  }

  public setSelected(selected: boolean): void {
    this.selected = selected;

    if (selected && !this.selectionRing) {
      // Create selection indicator
      const geometry = new THREE.RingGeometry(
        this.config.radius * 1.3,
        this.config.radius * 1.4,
        32
      );
      const material = new THREE.LineBasicMaterial({
        color: 0x00ff00,
        transparent: true,
        opacity: 0.8,
      });
      const points = [];
      const ringGeom = new THREE.RingGeometry(
        this.config.radius * 1.3,
        this.config.radius * 1.3,
        32
      );
      const posAttr = ringGeom.attributes.position;
      for (let i = 0; i < posAttr.count; i++) {
        points.push(new THREE.Vector3(
          posAttr.getX(i),
          posAttr.getY(i),
          posAttr.getZ(i)
        ));
      }
      points.push(points[0].clone()); // Close the ring

      const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);
      this.selectionRing = new THREE.Line(lineGeometry, material);
      this.mesh.add(this.selectionRing);
    } else if (!selected && this.selectionRing) {
      this.mesh.remove(this.selectionRing);
      this.selectionRing.geometry.dispose();
      (this.selectionRing.material as THREE.Material).dispose();
      this.selectionRing = undefined;
    }
  }

  public isSelected(): boolean {
    return this.selected;
  }

  public getPosition(): THREE.Vector3 {
    return this.mesh.position.clone();
  }

  public getVelocity(): THREE.Vector3 {
    return this.physicsBody.velocity.clone();
  }

  public setVelocity(velocity: THREE.Vector3): void {
    this.physicsBody.velocity.copy(velocity);
  }

  public clearOrbitTrail(): void {
    this.orbitTrail = [];
    if (this.orbitLine) {
      this.mesh.parent?.remove(this.orbitLine);
      this.orbitLine.geometry.dispose();
      (this.orbitLine.material as THREE.Material).dispose();
      this.orbitLine = undefined;
    }
  }

  public dispose(): void {
    // Clean up Three.js resources
    this.mainMesh.geometry.dispose();
    (this.mainMesh.material as THREE.Material).dispose();

    if (this.ringMesh) {
      this.ringMesh.geometry.dispose();
      (this.ringMesh.material as THREE.Material).dispose();
    }

    if (this.atmosphereMesh) {
      this.atmosphereMesh.geometry.dispose();
      (this.atmosphereMesh.material as THREE.Material).dispose();
    }

    if (this.glowMesh) {
      this.glowMesh.geometry.dispose();
      (this.glowMesh.material as THREE.Material).dispose();
    }

    if (this.cloudMesh) {
      this.cloudMesh.geometry.dispose();
      (this.cloudMesh.material as THREE.Material).dispose();
    }

    if (this.coronaMesh) {
      this.coronaMesh.geometry.dispose();
      (this.coronaMesh.material as THREE.Material).dispose();
    }

    if (this.selectionRing) {
      this.selectionRing.geometry.dispose();
      (this.selectionRing.material as THREE.Material).dispose();
    }

    this.clearOrbitTrail();
  }
}
